import { applyOffset, isSameMinute } from "./utils.js";

export const RESULTS = {
  unchanged: "unchanged",
  newDatePlusOffset: "newDatePlusOffset",
  offsetOnly: "offsetOnly",
  newDateOnly: "newDateOnly",
  inherited: "inherited",
};

export const RESULTS_META = {
  [RESULTS.newDateOnly]: {
    label: "New Date Applied",
    description: "A new due date was directly applied",
    style: {
      backgroundColor: "#10b981", // emerald-500
      color: "white",
    },
  },
  [RESULTS.newDatePlusOffset]: {
    label: "New Date + Offset Applied",
    description: "A new due date was applied with an offset adjustment",
    style: {
      backgroundColor: "#06b6d4", // cyan-500
      color: "white",
    },
  },
  [RESULTS.inherited]: {
    label: "Due Date Inherited",
    description: "Due date was inherited from the assessment",
    style: {
      backgroundColor: "#8b5cf6", // violet-500
      color: "white",
    },
  },
  [RESULTS.unchanged]: {
    label: "Due Date Unchanged",
    description: "The due date remains the same as before",
    style: {
      backgroundColor: "#6b7280", // gray-500
      color: "white",
    },
  },
  [RESULTS.offsetOnly]: {
    label: "Offset Applied",
    description: "An offset was applied to the existing due date",
    style: {
      backgroundColor: "#f59e0b", // amber-500
      color: "white",
    },
  },
};

const DEFAULT_STRATEGY = {
  id: "DEFAULT",
  text: "Please select an Apply Strategy.",
  clearExistingStudentExemptions: true,
  disableInputs: true,
  activitiesInputType: false,
  studentInputType: false,
  apply(date) {
    return { result: date, type: RESULTS.unchanged };
  },
};

function getCacheKey(date, dateType, newDate, offset) {
  return `${date.toISOString()}|${dateType}|${newDate.toISOString()}|${JSON.stringify(offset)
    }`;
}

const DATE_ASSESSMENT_AND_STUDENT_OFFSET = {
  text:
    "Apply new due date to a single assessment. Adjust selected students by offset based on new due date.",
  instructorsRestricted: true,
  clearExistingStudentExemptions: true,
  activitiesInputType: "date",
  activitiesMultiSelect: false,
  studentInputType: "offset",
  cache: new Map(),
  apply(date, dateType, newDate, offset) {
    if (date === undefined || date === null) {
      date = new Date(1970);
    }
    const cacheKey = getCacheKey(date, dateType, newDate, offset);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const data = {};
    if (
      offset.weeks === 0 &&
      offset.days === 0 &&
      offset.hours === 0 &&
      offset.minutes === 0
    ) {
      offset = null;
    }
    switch (true) {
      case (dateType === "activity" && newDate != null):
        data.type = isSameMinute(date, newDate)
          ? RESULTS.unchanged
          : RESULTS.newDateOnly;
        data.result = newDate;
        break;
      case (dateType === "activity"):
        data.type = RESULTS.unchanged;
        data.result = date;
        break;
      case (dateType === "student" && offset != null): {
        const dateWithOffset = applyOffset(newDate, offset);
        data.type = isSameMinute(date, dateWithOffset)
          ? RESULTS.unchanged
          : RESULTS.newDatePlusOffset;
        data.result = dateWithOffset;
        break;
      }
      case (dateType === "student"):
        data.type = RESULTS.inherited;
        data.result = newDate;
        break;
    }
    this.cache.set(cacheKey, data);
    return data;
  },
};

const STUDENT_OFFSET = {
  text:
    "Adjust selected students by offset based on the current assessment due date. Assessments level due dates cannot be changed.",
  clearExistingStudentExemptions: true,
  activitiesMultiSelect: true,
  studentInputType: "offset",
  cache: new Map(),
  apply(date, dateType, newDate, offset) {
    if (date === undefined || date === null) {
      date = new Date(1970);
    }
    if (dateType === "activity") {
      return { type: RESULTS.unchanged, result: date };
    }
    const cacheKey = getCacheKey(date, dateType, newDate, offset);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const data = {};
    if (
      offset.weeks === 0 &&
      offset.days === 0 &&
      offset.hours === 0 &&
      offset.minutes === 0
    ) {
      data.type = RESULTS.unchanged;
      data.result = date;
    } else {
      const dateWithOffset = applyOffset(date, offset);
      data.type = isSameMinute(date, dateWithOffset)
        ? RESULTS.unchanged
        : RESULTS.offsetOnly;
      data.result = dateWithOffset;
    }
    this.cache.set(cacheKey, data);
    return data;
  },
};

export const AVAILABLE_STRATEGIES = [
  DEFAULT_STRATEGY,
  DATE_ASSESSMENT_AND_STUDENT_OFFSET,
  STUDENT_OFFSET,
];
